/**
 * Speaker profiles -- group 11 (German engineering-led designs plus one
 * Japanese single-driver point source).
 *
 * This group is unusually good for the directivity and time-domain parts of the
 * model, because most of these designs exist specifically to solve a radiation
 * or step-response problem rather than a frequency-response one: two are
 * genuinely omnidirectional, two are bending-wave transducers with no crossover
 * in the critical band, two are fully active DSP systems, one is horn-loaded and
 * one is a single full-range driver with no crossover at all. The `responseTilt`
 * on each entry is the authored voicing offset; the anechoic baseline, impedance
 * and directivity around it are derived by the kit from the published alignment.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_11: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'TIDAL Audio',
    model: 'Contriva Diacera SE',
    price: usd(
      99000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair. TIDAL prices vary substantially with finish and market, so this is not an exact list figure.',
    ),
    year: 2015,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['diamond-dome', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [24, 20],
    hfExtension: 55000,
    portTuningHz: 26,
    crossovers: [
      { freqHz: 190, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 22 },
      { freqHz: 2400, slopeDbOct: 12, alignment: 'butterworth', offsetMm: 14 },
    ],
    responseTilt: [[20, 0], [60, 0.2], [150, -0.3], [500, 0], [1500, 0.2], [3000, -0.4], [7000, 0.3], [14000, 0.4], [20000, 0.2]],
    resonances: [[260, -52, 30], [640, -56, 34]],
    coverageAngleDeg: 95,
    recommendedWatts: [50, 500],
    dimensionsMm: [1290, 300, 470],
    massKg: 85,
    thdPercent: 0.2,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#2f6f8f',
    tags: ['germany', 'diamond-tweeter', 'ceramic-midrange', 'inert-cabinet'],
    notes:
      'A diamond dome pushes the first tweeter breakup mode above 50 kHz, so unlike a metal or soft dome there is no in-band resonance to notch out and the top octave stays flat rather than being shaped around a breakup peak; that is why the voicing offset here is fractional everywhere rather than a corrective dip. The cabinet is a heavily constrained-layer laminate, modelled as two very high-Q, very low-level panel modes at 260 and 640 Hz rather than the low-Q broad signature of a thin-wall design. Shallow 12 dB per octave acoustic slopes with deliberate driver offsets keep the crossover phase behaviour close to minimum-phase at the cost of wider driver overlap.',
    provenanceNote:
      'Driver complement (diamond tweeter, ceramic midrange), three-way ported alignment and cabinet construction follow TIDAL published product description. Sensitivity, impedance minimum, bass extension, crossover frequencies, slopes and driver offsets are ESTIMATED; TIDAL does not publish a full specification table. Anechoic response, directivity and cabinet resonance behaviour are MODELLED. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Ascendo',
    model: 'Live 15',
    price: usd(
      52000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair in the active DSP configuration; option content changes the price materially.',
    ),
    year: 2018,
    country: 'Germany',
    speakerType: 'horn',
    enclosure: 'horn-loaded',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    active: true,
    sensitivityDb: 98,
    nominalOhms: 8,
    minOhms: 6.5,
    bass: [28, 22],
    hfExtension: 22000,
    crossovers: [{ freqHz: 900, slopeDbOct: 48, alignment: 'digital-lr' }],
    responseTilt: [[20, 0], [45, 0.6], [90, 0.3], [300, -0.5], [800, 0.8], [1600, 1.2], [3500, -0.6], [8000, 0.4], [16000, -1.5]],
    resonances: [[135, -34, 11], [430, -38, 15]],
    coverageAngleDeg: 70,
    recommendedWatts: [1, 50],
    dimensionsMm: [1150, 520, 480],
    massKg: 95,
    thdPercent: 0.35,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'medium',
    confidence: 'ESTIMATED',
    accent: '#c88a3a',
    tags: ['germany', 'horn-waveguide', 'active-dsp', 'high-sensitivity', 'controlled-directivity'],
    notes:
      'A large-format 15 inch woofer crossed at 900 Hz into a horn-loaded compression driver, which is a directivity decision before it is a sensitivity decision: the 70 degree pattern is held nearly constant from the crossover upward, so the direct-to-reverberant ratio in the room model is far higher than any of the wide-dispersion designs in this group. Horn loading raises efficiency roughly 10 dB over a direct radiator of the same displacement, which drops power compression to a fraction of a dB but also makes air nonlinearity in the horn throat the dominant distortion mechanism, so harmonic distortion rises with SPL faster than it does for a cone. The steep 48 dB per octave DSP crossover keeps compression-driver excursion out of the region below the horn cutoff.',
    provenanceNote:
      'Horn-loaded two-way format, active DSP operation and the large-format woofer follow Ascendo published product description. Sensitivity, bass extension, crossover frequency and slope, coverage angle, dimensions and mass are ESTIMATED; Ascendo does not publish a complete anechoic specification. The quoted sensitivity is a driver-level figure and is not directly meaningful for an actively driven speaker -- it is retained so the engine can model the acoustic output correctly. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Backes & Muller',
    model: 'BM Line 25',
    price: usd(
      38000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair; Backes & Muller sells through limited distribution and pricing varies by market.',
    ),
    year: 2016,
    country: 'Germany',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['amt', 'dynamic'],
    driverCount: 4,
    wayCount: 3,
    active: true,
    sensitivityDb: 92,
    nominalOhms: 8,
    minOhms: 7,
    bass: [25, 20],
    hfExtension: 42000,
    crossovers: [
      { freqHz: 350, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 2500, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [50, 0.2], [120, -0.2], [400, 0], [1200, 0.3], [3000, 0.2], [6000, -0.3], [12000, 0.2], [20000, -0.8]],
    resonances: [[290, -50, 26], [700, -54, 30]],
    coverageAngleDeg: 100,
    recommendedWatts: [1, 10],
    dimensionsMm: [1250, 280, 380],
    massKg: 70,
    thdPercent: 0.08,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#6f7f6a',
    tags: ['germany', 'motional-feedback', 'sealed', 'active-dsp', 'amt'],
    notes:
      'The engineering signature here is sensor-based motional feedback on the bass drivers: an accelerometer on the cone closes a servo loop around the moving system, which suppresses the excursion-driven distortion that dominates every passive design below about 100 Hz and holds the sealed alignment to its target Q regardless of drive level. That is modelled as an unusually low 0.08 percent distortion figure with a much shallower excursion-related rise below the knee than the passive floorstanders in this group show. An AMT tweeter moves air by folding rather than pistonic motion, giving a lower moving mass per unit radiating area and useful output past 40 kHz, and the 48 dB per octave DSP crossovers keep each driver strictly inside its linear band.',
    provenanceNote:
      'Fully active operation, sealed alignment, motional-feedback bass control and the AMT high-frequency driver follow Backes & Muller published product description. Sensitivity, impedance, bass extension, crossover frequencies and slopes, dimensions, mass and distortion are ESTIMATED; the manufacturer does not publish a full anechoic specification. Quoted sensitivity and impedance are driver-level figures, since the amplifiers are internal and the input is line level. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Lyravox',
    model: 'Karlotta',
    price: usd(
      65000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair; Lyravox builds to order and finish options change the price.',
    ),
    year: 2019,
    country: 'Germany',
    speakerType: 'active',
    enclosure: 'sealed',
    drivers: ['amt', 'dynamic'],
    driverCount: 5,
    wayCount: 3,
    active: true,
    sensitivityDb: 90,
    nominalOhms: 8,
    minOhms: 6,
    bass: [22, 18],
    hfExtension: 40000,
    crossovers: [
      { freqHz: 260, slopeDbOct: 48, alignment: 'digital-lr' },
      { freqHz: 2200, slopeDbOct: 48, alignment: 'digital-lr' },
    ],
    responseTilt: [[20, 0], [60, 0.4], [200, 0], [800, -0.2], [2000, -0.4], [5000, -0.6], [10000, -0.9], [20000, -1.3]],
    resonances: [[310, -56, 34], [820, -58, 38]],
    coverageAngleDeg: 105,
    recommendedWatts: [1, 10],
    dimensionsMm: [1180, 340, 300],
    massKg: 110,
    thdPercent: 0.15,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#a08f7a',
    tags: ['germany', 'mineral-composite', 'active-dsp', 'downward-tilt', 'amt'],
    notes:
      'Where the Backes & Muller entry controls the driver with a servo loop, this one controls the enclosure: a cast mineral-composite cabinet at 110 kg for a 1.18 m tower pushes panel modes to a very low level and a very high Q, modelled here at -56 dB and Q 34 rather than the -50 dB, Q 26 of a braced wooden box. The voicing is the other difference -- a continuous downward tilt of about 1.3 dB from 200 Hz to 20 kHz, which is a monitoring convention rather than a flat-anechoic target and produces a noticeably different in-room balance from the essentially flat Backes & Muller offset. Corrections are applied in the DSP before the amplifiers, so unlike a passive network they cost no sensitivity and add no impedance interaction.',
    provenanceNote:
      'Fully active DSP operation, sealed alignment, mineral-composite cabinet construction and the AMT high-frequency driver follow Lyravox published product description. Sensitivity, bass extension, crossover frequencies and slopes, cabinet resonance level and Q, dimensions, mass and distortion are ESTIMATED; Lyravox does not publish a full anechoic specification. Quoted sensitivity and impedance are driver-level figures because the amplifiers are internal. Price is approximate and build-dependent.',
  }),

  makeSpeaker({
    mfr: 'Manger Audio',
    model: 'p1',
    price: usd(
      18000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair for the passive version; the active s1 variant is priced separately.',
    ),
    year: 2013,
    country: 'Germany',
    speakerType: 'floorstander',
    enclosure: 'sealed',
    drivers: ['bending-wave', 'dynamic'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 89,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [32, 26],
    hfExtension: 40000,
    crossovers: [{ freqHz: 330, slopeDbOct: 12, alignment: 'bessel', offsetMm: 18 }],
    responseTilt: [[20, 0], [60, -0.3], [150, 0.3], [400, 0.2], [1000, 0], [2500, -0.3], [6000, 0.3], [12000, -0.4], [20000, -1.0]],
    resonances: [[210, -40, 12], [520, -44, 16]],
    coverageAngleDeg: 110,
    recommendedWatts: [30, 250],
    dimensionsMm: [1080, 240, 360],
    massKg: 42,
    thdPercent: 0.25,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#7a6f9b',
    tags: ['germany', 'bending-wave', 'time-coherent', 'sealed', 'single-crossover'],
    notes:
      'The Manger transducer is a bending-wave radiator: the diaphragm is deliberately non-rigid, so radiation shifts progressively inward toward the voice coil as frequency rises instead of the whole cone moving as a piston. The practical consequence modelled here is that a single driver covers roughly 330 Hz to 40 kHz with no crossover anywhere in the region where the ear is most sensitive to arrival-time error, and the one crossover that does exist is placed low and given a shallow 12 dB per octave Bessel alignment specifically to minimise group-delay disturbance. A sealed alignment with a 32 Hz f3 gives a second-order rolloff, so the phase rotation through the bass is half that of a ported design of similar extension.',
    provenanceNote:
      'The bending-wave transducer, its stated operating band and the single low crossover follow Manger Audio published product description. Sensitivity, impedance minimum, bass extension, enclosure alignment, crossover slope and alignment type, dimensions and mass are ESTIMATED; the sealed alignment in particular is inferred from the stated time-domain design goals rather than quoted. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'German Physiks',
    model: 'HRS-130',
    price: usd(
      32000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair; finish options change the price significantly.',
    ),
    year: 2012,
    country: 'Germany',
    speakerType: 'omnidirectional',
    enclosure: 'ported',
    drivers: ['bending-wave', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 87,
    nominalOhms: 4,
    minOhms: 3.5,
    bass: [29, 24],
    hfExtension: 24000,
    portTuningHz: 30,
    crossovers: [{ freqHz: 190, slopeDbOct: 12, alignment: 'first-order' }],
    responseTilt: [[20, 0], [50, 0.5], [120, 0.2], [400, -0.4], [1000, 0.2], [3000, -0.5], [8000, -1.0], [14000, -1.6], [20000, -2.0]],
    resonances: [[160, -38, 10], [480, -42, 14]],
    coverageAngleDeg: 340,
    recommendedWatts: [50, 300],
    dimensionsMm: [1180, 320, 320],
    massKg: 45,
    thdPercent: 0.4,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#4f8f6f',
    tags: ['germany', 'omnidirectional', 'ddd-driver', 'bending-wave', 'wide-band-single-driver'],
    notes:
      'The DDD driver is a bending-wave cone radiating over 360 degrees in the horizontal plane from about 190 Hz upward, so the speaker puts far more energy into the reverberant field than a forward-firing design and the room becomes part of the transfer function rather than a disturbance to it -- this profile therefore carries a 340 degree coverage angle and high room coupling. Because the radiating source is omnidirectional but physically small, on-axis response falls gently through the top two octaves as power response stays flat, which is the -2 dB voicing offset at 20 kHz; correcting that on axis would over-energise the reverberant field. A single first-order crossover at 190 Hz keeps the entire midrange and treble on one radiator.',
    provenanceNote:
      'The DDD bending-wave driver, its omnidirectional radiation, the single low crossover and the ported bass alignment follow German Physiks published product description. Sensitivity, impedance minimum, bass extension, port tuning, crossover frequency and slope, dimensions and mass are ESTIMATED. On-axis versus power-response behaviour is MODELLED from the stated omnidirectional radiation pattern, not measured. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Duevel',
    model: 'Bella Luna Diamante',
    price: usd(
      16000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair; the model has been sold at a range of prices across markets.',
    ),
    year: 2010,
    country: 'Germany',
    speakerType: 'omnidirectional',
    enclosure: 'ported',
    drivers: ['compression-driver', 'dynamic'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 89,
    nominalOhms: 6,
    minOhms: 4.5,
    bass: [30, 25],
    hfExtension: 22000,
    portTuningHz: 32,
    crossovers: [{ freqHz: 600, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, 0], [45, 0.8], [100, -0.4], [300, 0.5], [700, 1.0], [1500, -0.8], [3000, 0.7], [8000, -0.5], [16000, -1.8]],
    resonances: [[190, -32, 9], [560, -36, 13], [1750, -40, 18]],
    coverageAngleDeg: 340,
    recommendedWatts: [20, 200],
    dimensionsMm: [1150, 380, 380],
    massKg: 38,
    thdPercent: 0.5,
    harmonicShape: HARMONIC_SHAPES.horn,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b5564f',
    tags: ['germany', 'omnidirectional', 'reflector-horn', 'upward-firing'],
    notes:
      'Omnidirectional by a completely different mechanism from the German Physiks entry: both drivers fire upward into machined reflector cones that redistribute the wavefront through 360 degrees, so this is horn loading pressed into service as a dispersion device. The reflector and its throat impose a real acoustic signature -- the voicing offset carries a broad +1 dB lift around 700 Hz and a compensating dip near 1.5 kHz, which is the classic reflector-horn resonance pattern, and three relatively low-Q structural resonances are modelled rather than two. Distortion uses a horn structure with second harmonic rising faster with SPL than a direct radiator, and the crossover at 600 Hz sits inside the vocal band, unlike the 190 Hz crossover of the bending-wave omni.',
    provenanceNote:
      'Omnidirectional operation via upward-firing drivers and reflector cones, the two-way format and the ported bass cabinet follow Duevel published product description. Sensitivity, impedance, bass extension, port tuning, crossover frequency and slope, resonance behaviour, dimensions and mass are ESTIMATED. The reflector-related response features in the voicing offset are MODELLED from the geometry, not measured. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Eclipse',
    model: 'TD712zMK2',
    price: usd(
      8000,
      'APPROXIMATE',
      2023,
      'Approximate US retail per pair including the dedicated stands; Eclipse has sold the model at varying prices by region.',
    ),
    year: 2010,
    country: 'Japan',
    speakerType: 'standmount',
    enclosure: 'sealed',
    drivers: ['dynamic'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 84,
    nominalOhms: 6,
    minOhms: 5,
    bass: [45, 38],
    hfExtension: 22000,
    crossovers: [],
    responseTilt: [[20, 0], [60, -0.6], [150, -0.3], [500, 0.2], [1500, 0.4], [3000, 0.6], [6000, -0.4], [10000, -1.2], [20000, -2.4]],
    resonances: [[420, -46, 20]],
    coverageAngleDeg: 150,
    recommendedWatts: [10, 100],
    dimensionsMm: [1130, 260, 380],
    massKg: 24,
    thdPercent: 0.45,
    roomCoupling: 'low',
    confidence: 'ESTIMATED',
    accent: '#9aa7b0',
    tags: ['japan', 'full-range', 'no-crossover', 'point-source', 'sealed'],
    notes:
      'One 12 cm full-range driver, no crossover network at all, in a sealed egg-shaped shell whose curvature eliminates parallel internal surfaces and whose driver is anchored to a mass rather than to the cabinet wall, so reaction force is not transmitted into the enclosure. The model therefore carries a single cabinet resonance instead of two or three, and a step response with no crossover-induced phase rotation anywhere in the band -- the design trades bandwidth and sensitivity for that. The costs are explicit in the numbers: 84 dB sensitivity, a 45 Hz f3, and rising distortion at low frequency because one small cone carries the entire range, plus a falling top octave as the cone diameter becomes comparable to the wavelength.',
    provenanceNote:
      'Single full-range driver, absence of any crossover, sealed shell construction and the anchored-driver mounting follow Eclipse published product description. Sensitivity, impedance, bass extension, dimensions, mass and distortion are ESTIMATED; the published low-frequency figure is quoted at a different reference than -3 dB, so the bass extension here is a conversion rather than a quoted specification. Price is approximate and stand-dependent.',
  }),
];
